import { useCallback, useEffect, useRef, useState } from "react";

import {
  API_DOCUMENT_APPROVER,
  API_GET_EMPLOYEE_FILE,
  API_PHASE2_LIST,
  phase2ApproveUrl,
  phase2RejectUrl,
} from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, getApi, putApi, putRawText } from "../../../../utils/api.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { SQE_PHASE2, normalizeSqePhase2Row, sqeRows } from "../../onboarding/sqe/sqe-onboarding-api.ts";
import { useUser } from "../../../../lib/rbac/useUser.ts";
import {
  parsePhase2ListResponse,
  type ApprovalCenterStats,
  type Phase2ListRow,
} from "../schemas/phase2-list.schema.ts";

export type Phase2ListFilters = {
  status?: string;
  employeeId?: string;
  region?: string;
  fromDate?: string | null;
  toDate?: string | null;
};

export type Phase2ListQuery = Phase2ListFilters & {
  page: number;
  pageSize: number;
};

const EMPTY_STATS: ApprovalCenterStats = {
  total: 0,
  pending: 0,
  pendingPostJoining: 0,
  pendingReview: 0,
  pendingInsurance: 0,
  needsCorrection: 0,
  pendingPreJoining: 0,
  approved: 0,
  rejected: 0,
};

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

function buildListUrl(query: Phase2ListQuery, isSqe = false): string {
  const params = new URLSearchParams();
  if (query.status != null && query.status !== "") {
    params.set("status", query.status);
  }
  if (query.employeeId != null && query.employeeId !== "") {
    params.set("employeeId", query.employeeId);
  }
  if (!isSqe && query.region != null && query.region !== "") {
    params.set("region", query.region);
  }
  params.set("currentPage", String(isSqe ? Math.max(0, query.page - 1) : Math.max(1, query.page)));
  params.set("pageSize", String(Math.max(1, query.pageSize)));
  return `${isSqe ? `${SQE_PHASE2}/list` : API_PHASE2_LIST}?${params.toString()}`;
}

/** Unfiltered list call used only to load global summary counts for stat cards. */
function buildStatsUrl(): string {
  const params = new URLSearchParams();
  params.set("currentPage", "1");
  params.set("pageSize", "1");
  return `${API_PHASE2_LIST}?${params.toString()}`;
}

function buildPendingPreJoiningStatsUrl(): string {
  const params = new URLSearchParams();
  params.set("status", "PENDING_PREJOINING");
  params.set("currentPage", "1");
  params.set("pageSize", "10");
  return `${API_PHASE2_LIST}?${params.toString()}`;
}

function isAbortError(e: unknown): boolean {
  return e instanceof Error && e.name === "AbortError";
}

export function usePhase2List(
  filters: Phase2ListFilters,
  page: number,
  pageSize: number,
) {
  const isSqe = useSqeLabs();
  const user = useUser();
  const financeOnly = user?.roles.includes("Expense_Mgmt_Finance") && !user.roles.some((role) => role === "HR" || role === "SUPERADMIN");
  const [rows, setRows] = useState<Phase2ListRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [stats, setStats] = useState<ApprovalCenterStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const listRequestId = useRef(0);

  const fetchStats = useCallback(async (signal?: AbortSignal) => {
    setStatsLoading(true);
    try {
      if (isSqe) {
        if (financeOnly) {
          const { data } = await getApi<unknown>(buildListUrl({ status: "APPROVED", page: 1, pageSize: 1 }, true), accessToken(), "json", signal);
          if (!signal?.aborted) setStats({ ...EMPTY_STATS, total: sqeRows(data).totalElements, approved: sqeRows(data).totalElements });
          return;
        }
        const statuses = ["PENDING_PREJOINING", "PENDING", "APPROVED", "REJECTED"];
        const results = await Promise.all(statuses.map((status) => getApi<unknown>(buildListUrl({ status, page: 1, pageSize: 1 }, true), accessToken(), "json", signal)));
        if (signal?.aborted) return;
        const counts = results.map(({ data }) => sqeRows(data).totalElements);
        setStats({ total: counts.reduce((sum, count) => sum + count, 0), pendingPreJoining: counts[0], pending: counts[1], approved: counts[2], rejected: counts[3] });
        return;
      }
      const { data } = await getApi<unknown>(
        buildStatsUrl(),
        accessToken(),
        "json",
        signal,
      );
      const { data: preJoiningData } = await getApi<unknown>(
        buildPendingPreJoiningStatsUrl(),
        accessToken(),
        "json",
        signal,
      );
      if (signal?.aborted) return;
      const { summary } = parsePhase2ListResponse(data);
      const preJoining = parsePhase2ListResponse(preJoiningData);
      setStats({
        ...summary,
        pendingPreJoining: preJoining.totalElements,
      });
    } catch (e) {
      if (isAbortError(e) || signal?.aborted) return;
      setStats(EMPTY_STATS);
    } finally {
      if (!signal?.aborted) setStatsLoading(false);
    }
  }, [isSqe, financeOnly]);

  const refetch = useCallback(
    async (signal?: AbortSignal) => {
      const requestId = ++listRequestId.current;
      setLoading(true);
      setError(null);
      try {
        const { data } = await getApi<unknown>(
          buildListUrl({ ...filters, page, pageSize }, isSqe),
          accessToken(),
          "json",
          signal,
        );
        if (signal?.aborted || (isSqe && requestId !== listRequestId.current)) return;
        const sqeList = isSqe ? sqeRows(data) : null;
        const parsed = sqeList
          ? parsePhase2ListResponse({ data: sqeList.rows.map(normalizeSqePhase2Row), totalElements: sqeList.totalElements })
          : parsePhase2ListResponse(data);
        setRows(parsed.rows);
        setTotalElements(parsed.totalElements);
      } catch (e) {
        if (isAbortError(e) || signal?.aborted || (isSqe && requestId !== listRequestId.current)) return;
        const msg =
          e instanceof ApiError ? e.message : "Failed to load approval requests.";
        setError(msg);
        setRows([]);
        setTotalElements(0);
      } finally {
        if (!signal?.aborted && (!isSqe || requestId === listRequestId.current)) setLoading(false);
      }
    },
    [filters, page, pageSize, isSqe],
  );

  useEffect(() => {
    const ac = new AbortController();
    void refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  useEffect(() => {
    const ac = new AbortController();
    void fetchStats(ac.signal);
    return () => ac.abort();
  }, [fetchStats]);

  const approve = useCallback(async (employeeId: number) => {
    if (isSqe) await putRawText(`${SQE_PHASE2}/approve?employeeId=${encodeURIComponent(String(employeeId))}`, accessToken(), "Approved by HR");
    else await putApi(phase2ApproveUrl(employeeId), accessToken(), {});
  }, [isSqe]);

  const reject = useCallback(async (employeeId: number, comment: string) => {
    if (isSqe) await putRawText(`${SQE_PHASE2}/reject?employeeId=${encodeURIComponent(String(employeeId))}`, accessToken(), comment);
    else await putApi(phase2RejectUrl(employeeId), accessToken(), { comment });
  }, [isSqe]);

  const approveDocument = useCallback(async (documentId: number) => {
    const url = `${API_DOCUMENT_APPROVER}?documentId=${encodeURIComponent(String(documentId))}&status=approved`;
    await putApi(url, accessToken(), {});
  }, []);

  const rejectDocument = useCallback(async (documentId: number, reason: string) => {
    const url = `${API_DOCUMENT_APPROVER}?documentId=${encodeURIComponent(String(documentId))}&reason=${encodeURIComponent(reason)}&status=rejected`;
    await putApi(url, accessToken(), {});
  }, []);

  const fetchFileBlob = useCallback(async (filepath: string, signal?: AbortSignal) => {
    const url = `${API_GET_EMPLOYEE_FILE}?filepath=${encodeURIComponent(filepath)}`;
    const { data } = await getApi<Blob>(url, accessToken(), "blob", signal);
    return data;
  }, []);

  const fetchChequeBlob = useCallback(
    async (row: Phase2ListRow, signal?: AbortSignal) => {
      const path = row.bankDetails.cancelledChequePath;
      if (!path) throw new Error("No cancelled cheque path.");
      return fetchFileBlob(path, signal);
    },
    [fetchFileBlob],
  );

  const refreshAll = useCallback(async () => {
    await Promise.all([refetch(), fetchStats()]);
  }, [refetch, fetchStats]);

  return {
    rows,
    totalElements,
    stats,
    loading,
    statsLoading,
    error,
    refetch,
    refreshAll,
    fetchStats,
    approve,
    reject,
    approveDocument,
    rejectDocument,
    fetchChequeBlob,
    fetchFileBlob,
  };
}
